import { IPublicClientApplication } from "@azure/msal-browser";
import { AxiosRequestConfig } from "axios";
import GenericUtils from "../utils/GenericUtils";
import axiosApi from "./Axios";

const baseUrl = process.env.REACT_APP_URI;

let currentTimestamp = Math.floor(new Date().getTime() / 1000);
const refreshRate = 900;

export const getUrl = (url: string): string => {
    return `${baseUrl}${url}`;
};

let numberOfRequests = 0;

const loading = "loading";
const loadingContainerId = "loading-container-id";
const loadingContainerNone = "container-none";
axiosApi.interceptors.request.use(
    // eslint-disable-next-line func-names
    function (config) {
        // spinning start to show
        // UPDATE: Add this code to show global loading indicator
        if (!numberOfRequests) {
            const loadingContainer =
                document.getElementById(loadingContainerId);
            loadingContainer?.classList.remove(loadingContainerNone);
            loadingContainer?.classList.add(loading);
        }
        numberOfRequests++;

        return config;
    },
    // eslint-disable-next-line func-names
    function (error) {
        return Promise.reject(error);
    }
);

axiosApi.interceptors.response.use(
    // eslint-disable-next-line func-names
    function (response) {
        // spinning hide
        // UPDATE: Add this code to hide global loading indicator
        if (numberOfRequests === 1) {
            const loadingContainer =
                document.getElementById(loadingContainerId);
            loadingContainer?.classList.remove(loading);
            loadingContainer?.classList.add(loadingContainerNone);
        }
        numberOfRequests--;

        return response;
    },
    // eslint-disable-next-line func-names
    function (error) {
        if (numberOfRequests === 1) {
            const loadingContainer =
                document.getElementById(loadingContainerId);
            loadingContainer?.classList.remove(loading);
            loadingContainer?.classList.add(loadingContainerNone);
        }
        numberOfRequests--;

        return Promise.reject(error);
    }
);

export const getHeaders = (
    token: string,
    params?: boolean,
    instance?: IPublicClientApplication,
    isArrayBuffer?: boolean,
    contentType = "application/json"
): AxiosRequestConfig => {
    const actualTimestamp = Math.floor(new Date().getTime() / 1000);

    if (
        actualTimestamp - currentTimestamp > refreshRate &&
        instance !== undefined
    ) {
        currentTimestamp = actualTimestamp;
        GenericUtils.getTokenMicroservices(instance);
    }

    const timestamp = { _timestamp: new Date() };
    const queryParams = params && timestamp;

    let header: AxiosRequestConfig = {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": contentType,
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
        },
        params: queryParams,
    };

    if (isArrayBuffer)
        header = {
            responseType: "arraybuffer",
            ...header,
        };

    return header;
};

export const getHeadersWithData = (
    token: string,
    payload: boolean,
    instance?: IPublicClientApplication
): AxiosRequestConfig => {
    const actualTimestamp = Math.floor(new Date().getTime() / 1000);

    if (
        actualTimestamp - currentTimestamp > refreshRate &&
        instance !== undefined
    ) {
        currentTimestamp = actualTimestamp;
        GenericUtils.getTokenMicroservices(instance);
    }

    return {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
        },
        data: payload,
    };
};

export const axiosGetRequest = async (
    url: string,
    token: string,
    instance?: IPublicClientApplication,
    params?: boolean
) => {
    return axiosApi
        .get(url, getHeaders(token, params, instance))
        .then((result) => result)
        .catch((error) => {
            if (error.response.status === 401 && instance)
                GenericUtils.logout(instance);
            if (error.response.status === 403 && instance)
                GenericUtils.logout(instance);

            return error.response;
        });
};

export const axiosPostRequest = async (
    url: string,
    params: any,
    token: string,
    instance?: IPublicClientApplication,
    isArrayBuffer?: boolean
) => {
    return axiosApi
        .post(
            url,
            params,
            getHeaders(token, undefined, instance, isArrayBuffer)
        )
        .then((result) => result)
        .catch((error) => {
            if (error.response.status === 401 && instance)
                GenericUtils.logout(instance);
            if (error.response.status === 403 && instance)
                GenericUtils.logout(instance);
            return error.response;
        });
};

export const axiosPutRequest = async (
    url: string,
    params: any,
    token: string,
    instance?: IPublicClientApplication
) => {
    return axiosApi
        .put(url, params, getHeaders(token, undefined, instance))
        .then((result) => result)
        .catch((error) => {
            if (error.response.status === 401 && instance)
                GenericUtils.logout(instance);
            if (error.response.status === 403 && instance)
                GenericUtils.logout(instance);

            return error.response;
        });
};

export const axiosPatchRequest = async (
    url: string,
    params: any,
    token: string,
    instance?: IPublicClientApplication
) => {
    return axiosApi
        .patch(url, params, getHeaders(token, undefined, instance))
        .then((result) => result)
        .catch((error) => {
            if (error.response.status === 401 && instance)
                GenericUtils.logout(instance);
            if (error.response.status === 403 && instance)
                GenericUtils.logout(instance);

            return error.response;
        });
};

export const axiosDelRequest = async (
    url: string,
    token: string,
    instance?: IPublicClientApplication
) => {
    return axiosApi
        .delete(url, getHeaders(token, undefined, instance))
        .then((result) => result)
        .catch((error) => {
            if (error.response.status === 401 && instance)
                GenericUtils.logout(instance);
            if (error.response.status === 403 && instance)
                GenericUtils.logout(instance);

            return error.response;
        });
};

export const axiosDelWithParamRequest = async (
    url: string,
    params: any,
    token: string,
    instance?: IPublicClientApplication
) => {
    return axiosApi
        .delete(url, getHeadersWithData(token, params, instance))
        .then((result) => result)
        .catch((error) => {
            if (error.response.status === 401 && instance)
                GenericUtils.logout(instance);
            if (error.response.status === 403 && instance)
                GenericUtils.logout(instance);

            return error.response;
        });
};

export const axiosPutRequestUsingTxtHtml = async (
    url: string,
    params: any,
    token: string,
    instance?: IPublicClientApplication
) => {
    return axiosApi
        .put(
            url,
            params,
            getHeaders(token, undefined, instance, false, "text/html")
        )
        .then((result) => result)
        .catch((error) => {
            if (error.response.status === 401 && instance)
                GenericUtils.logout(instance);
            if (error.response.status === 403 && instance)
                GenericUtils.logout(instance);

            return error.response;
        });
};
